import { useEffect, useState } from "react";
import axios from "../../axios";
import { ProductListResponseDto, ProductResponseDto } from "./dtos";

enum STATUS {
  LOADING,
  ERROR,
  SUCCESS,
}

function useProduct() {
  const [page, setPage] = useState<number>(1);
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [filter, setFilter] = useState<string | null>();
  const [status, setStatus] = useState<STATUS>(STATUS.SUCCESS);
  const [refetch, setRefetch] = useState(0);

  const [prods, setProds] = useState<ProductResponseDto[]>([]);

  const isEmptyList = prods.length === 0;

  const showSkeletons = status === STATUS.LOADING && isEmptyList;
  const showFooter = status === STATUS.LOADING && !isEmptyList;
  const showEmptyList =
    status === STATUS.SUCCESS && filter == null && isEmptyList;
  const showEmptySearch =
    status === STATUS.SUCCESS && filter != null && isEmptyList;

  const showError = status === STATUS.ERROR;

  const loadMoreProducts = () => {
    status === STATUS.SUCCESS && nextPage !== null && setPage(nextPage);
  };

  const doRefetch = () => {
    setRefetch((prev) => prev + 1);
  };

  const reset = () => {
    setProds([]);
    setPage(1);
  };

  const applyFilter = (value: string | null) => {
    reset();
    setFilter(value);
  };

  const removeFilter = () => {
    reset();
    setFilter(null);
  };

  useEffect(() => {
    const fetchProducts = async (page: number = 1) => {
      setStatus(STATUS.LOADING);
      try {
        const filterQuery = filter ? `&q=${filter}` : "";
        const { data } = await axios.get<ProductListResponseDto>(
          `products?page=${page}&perPage=20${filterQuery}`
        );
        setStatus(STATUS.SUCCESS);
        setProds((prev) => [...prev, ...data.results]);
        setNextPage(data.pagination.nextPage);
      } catch (error) {
        setStatus(STATUS.ERROR);
      }
    };
    fetchProducts(page);
  }, [page, filter, refetch]);

  return {
    prods,
    filter,
    showSkeletons,
    showEmptyList,
    showEmptySearch,
    showFooter,
    showError,
    refetch: doRefetch,
    loadMoreProducts,
    applyFilter,
    removeFilter,
  };
}

export default useProduct;
